/**
 * 中大奖榜的展示逻辑。
 *
 * <p>门槛、排序、倍数的**计算**都在后端（见 BIG_WINS_BOARD_BACKEND_TODO.md）。
 * 这里只负责把后端给的数字变成屏幕上的字，以及把「没有倍数」这件事表达对。
 */

import { localListTime } from '../../shared/lib/datetime.ts';

export type BigWinGame = 'milhar' | 'milhar-hora' | 'raffle';

/**
 * 倍数标签。
 *
 * @returns `null` 表示这一笔没有倍数（免费票），调用方应当渲染 FREE 而不是数字
 */
export function multipleLabel(multiple: number | null | undefined): string | null {
  // null / undefined 必须显式挡掉：Number(null) 是 0，不是 NaN，
  // 于是免费票会渲染成「×0」—— 一个看起来完全合理、意思却完全相反的标签。
  if (multiple === null || multiple === undefined) return null;
  const value = Number(multiple);
  if (!Number.isFinite(value) || value <= 0) return null;
  return `×${Math.round(value).toLocaleString('pt-BR')}`;
}

/**
 * 一行的「什么时候」——今天是 `09:00`，更早是 `03/07 09:00`。
 *
 * <p><b>这里原先渲染的是期号（`#09` / `#169`）。</b>期号是「那一天的第 N 期」，
 * 它唯一的用途是让**中奖者本人**拿去核对自己的投注历史（历史一直是这么编号的，
 * 产品口径 UX_REVIEW_2026-08-18 F3）。而中大奖榜的观众几乎全是旁观者 —— 给旁观者
 * 看一个为当事人设计的编号，读不懂是必然的（2026-09-03 用户反馈）。
 *
 * <p>而且它答错了问题。旁观者想知道的是「这事多近」，期号答的是「那天的第几期」，
 * 且**不带日期**：榜单跨 7 天到 30 天，同一个 `#09` 在一张榜上出现三十次都不奇怪。
 * 更早的写法还是分裂的 —— Milhar 两档给期号、Draw 给一个裸时钟，两套记法回答同
 * 一个问题，两套都没有日期。现在三种玩法说同一句话。
 *
 * <p>时间戳必须是**开奖窗口的起点**（`roundStartsAt`，即 `rounds.cutoff_ts`），不能
 * 用 `wonAt`：结算发生在开奖之后、也就是窗口关闭之后，用它每一行都会晚一格。
 *
 * <p><b>时刻以观看者设备时区为准</b>，不是后端按国家注册时区烤出来的字符串 ——
 * 同一条产品裁决，马瑙斯（UTC-4）和圣保罗的玩家必须各自看到自己墙上的钟
 * （BIG_WINS_BOARD_DESIGN.md §8）。复用钱包流水列表已经在用的
 * {@link localListTime}，所以两个列表的时间格式不会各走各的。
 *
 * <p>未知玩法（后端先于客户端上了新玩法）不再需要单独一条退路。老实现要那条退路，
 * 是因为**编号**需要知道开奖节奏 —— 拿 5 分钟的节奏给一个不认识的玩法编号，会编出
 * 一个看起来完全正常的错号。格式化一个时间戳不需要知道节奏，那个风险连同那条分支
 * 一起没有了。
 *
 * @param nowSeconds 比较基准由调用方给，这个函数保持是纯的（与 localListTime 同一
 *                   约定）—— 否则它的返回值会随挂钟漂移，测不住。
 */
export function bigWinWhenLabel(
  row: { game?: string; roundStartsAt?: number | null; roundLabel?: string },
  nowSeconds: number,
): string {
  const startsAt = Number(row.roundStartsAt || 0);
  // 没有时间戳时退回服务端的标签，而不是渲染一个 `--` —— 有一个读不懂的标签，
  // 也好过让这一行看起来像坏了。
  if (!(startsAt > 0)) return String(row.roundLabel || '');
  return localListTime(startsAt, nowSeconds);
}

export function gameLabelKey(game: string) {
  if (game === 'milhar-hora') return 'bigWinsGameMilharHora' as const;
  // 兜底走 Milhar：后端先于客户端上了新玩法时，这一行仍要能渲染出来，而不是
  // 让整个榜白屏。兜底到 Draw 已经不成立了 —— 抽奖不在这个榜上（设计稿 §2.3），
  // 拿它当兜底会给一条新玩法贴一个明确错误的标签。
  return 'bigWinsGameMilhar' as const;
}

/**
 * 距今多少天 —— 头奖卡上那句「18 天前」。
 *
 * <p>这句话是整张卡的情绪引擎，也是它和大厅那句「N 天没出头奖 · 奖池 R$ X」
 * 对上的地方：奖池在涨、上一次中奖越来越远，两个数字互相印证。
 *
 * <p>按**本地日历天**算差，不是按 86400 秒整除。昨天 23:00 到今天 01:00 只隔
 * 两小时，但那是「昨天」不是「今天」—— 除法会说 0 天，玩家会读成「刚刚」。
 *
 * @returns 0 = 今天，1 = 昨天，以此类推；时间戳缺失或在未来时返回 null
 */
export function daysAgo(seconds: number | null | undefined, nowSeconds: number): number | null {
  const then = Number(seconds || 0);
  if (!(then > 0) || !(nowSeconds > 0)) return null;
  const midnight = (sec: number) => {
    const d = new Date(sec * 1000);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const diff = Math.round((midnight(nowSeconds) - midnight(then)) / 86_400_000);
  // 时钟歪了、或者后端给了个未来的时间戳。宁可不说，也不要说「-3 天前」。
  return diff < 0 ? null : diff;
}
